<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <card :title="$t('timeRegistrationGroups.title')" :on-add-object="openCreate">
          <div v-if="!loaded" class="text-center text-secondary py-4">{{ $t('common.loading') }}</div>
          <div v-else-if="groups.length === 0" class="text-center text-secondary py-4">
            {{ $t('timeRegistrationGroups.noGroups') }}
          </div>
          <el-table v-else :data="groups" row-key="id" style="width:100%">
            <el-table-column :label="$t('timeRegistrationGroups.name')" prop="name" min-width="140" />
            <el-table-column :label="$t('timeRegistrationGroups.description')" prop="description" min-width="200" />
            <el-table-column :label="$t('timeRegistrationGroups.members')" width="110">
              <template #default="scope">{{ scope.row.memberCount }}</template>
            </el-table-column>
            <el-table-column :label="$t('common.status')" width="110">
              <template #default="scope">
                <el-tag v-if="scope.row.isArchived" type="info" size="small">{{ $t('timeRegistrationGroups.archived') }}</el-tag>
                <el-tag v-else type="success" size="small">{{ $t('timeRegistrationGroups.active') }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="" width="260" align="right">
              <template #default="scope">
                <el-button size="small" @click="openMembers(scope.row)">
                  <i class="material-icons me-1" style="font-size:1rem;vertical-align:middle">group</i>
                  {{ $t('timeRegistrationGroups.manageMembers') }}
                </el-button>
                <el-button size="small" @click="openEdit(scope.row)">
                  <i class="material-icons" style="font-size:1rem">edit</i>
                </el-button>
                <el-button size="small" type="danger" @click="confirmDelete(scope.row)">
                  <i class="material-icons" style="font-size:1rem">delete</i>
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <div class="mt-3">
            <el-checkbox v-model="includeArchived" @change="fetchGroups">{{ $t('timeRegistrationGroups.includeArchived') }}</el-checkbox>
          </div>
        </card>
      </div>
    </div>

    <!-- Create / edit dialog -->
    <el-dialog v-model="showForm" :title="editMode ? $t('timeRegistrationGroups.editGroup') : $t('timeRegistrationGroups.newGroup')" width="480">
      <div class="mb-3">
        <label class="form-label">{{ $t('timeRegistrationGroups.name') }} <span class="text-danger">*</span></label>
        <el-input v-model="form.name" maxlength="100" />
      </div>
      <div class="mb-3">
        <label class="form-label">{{ $t('timeRegistrationGroups.description') }}</label>
        <el-input v-model="form.description" type="textarea" :rows="2" maxlength="500" />
      </div>
      <div v-if="editMode" class="mb-2">
        <el-checkbox v-model="form.isArchived">{{ $t('timeRegistrationGroups.archived') }}</el-checkbox>
      </div>
      <template #footer>
        <el-button @click="showForm = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="saving" @click="submitForm">{{ $t('common.submit') }}</el-button>
      </template>
    </el-dialog>

    <!-- Members dialog -->
    <el-dialog v-model="showMembers" :title="membersDialogTitle" width="560">
      <div class="mb-3">
        <label class="form-label">{{ $t('timeRegistrationGroups.addMember') }}</label>
        <div class="d-flex gap-2">
          <el-select v-model="selectedUserToAdd" filterable class="flex-grow-1" :placeholder="$t('timeRegistrationGroups.pickUser')">
            <el-option v-for="u in assignableUsers" :key="u.id" :label="`${u.firstName || ''} ${u.lastName || ''}`.trim() + (u.email ? ` (${u.email})` : '')" :value="u.id" />
          </el-select>
          <el-button type="primary" :disabled="!selectedUserToAdd || savingMember" @click="addMember">
            <i class="material-icons" style="font-size:1rem">person_add</i>
          </el-button>
        </div>
        <small class="text-secondary">{{ $t('timeRegistrationGroups.oneGroupPerUserHint') }}</small>
      </div>
      <el-divider />
      <div v-if="!activeGroup || activeGroup.members.length === 0" class="text-center text-secondary py-2">
        {{ $t('timeRegistrationGroups.noMembers') }}
      </div>
      <el-table v-else :data="activeGroup.members" size="small">
        <el-table-column :label="$t('timeRegistrationGroups.name')" prop="userName" />
        <el-table-column :label="$t('users.email')" prop="email" />
        <el-table-column label="" width="80" align="right">
          <template #default="scope">
            <el-button size="small" type="danger" @click="removeMember(scope.row)">
              <i class="material-icons" style="font-size:1rem">person_remove</i>
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <template #footer>
        <el-button @click="showMembers = false">{{ $t('common.close') }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import api from '../../services/api';
import Card from '../../components/Card.vue';
import { ElDialog, ElInput, ElSelect, ElOption, ElButton, ElTable, ElTableColumn, ElTag, ElCheckbox, ElDivider, ElMessageBox } from 'element-plus';

export default {
  name: 'ManageGroups',
  components: {
    Card,
    ElDialog, ElInput, ElSelect, ElOption, ElButton, ElTable, ElTableColumn, ElTag, ElCheckbox, ElDivider,
  },
  data() {
    return {
      loaded: false,
      groups: [],
      users: [],
      includeArchived: false,
      showForm: false,
      saving: false,
      editMode: false,
      form: { id: null, name: '', description: '', isArchived: false },
      showMembers: false,
      activeGroupId: null,
      selectedUserToAdd: null,
      savingMember: false,
    };
  },
  computed: {
    activeGroup() {
      return this.groups.find(g => g.id === this.activeGroupId) || null;
    },
    membersDialogTitle() {
      return this.activeGroup
        ? `${this.$t('timeRegistrationGroups.manageMembers')} — ${this.activeGroup.name}`
        : this.$t('timeRegistrationGroups.manageMembers');
    },
    assignableUsers() {
      // Exclude users already in the active group
      if (!this.activeGroup) return this.users;
      const memberIds = new Set(this.activeGroup.members.map(m => m.userId));
      return this.users.filter(u => !memberIds.has(u.id));
    },
  },
  created() {
    this.fetchUsers();
    this.fetchGroups();
  },
  methods: {
    async fetchGroups() {
      this.loaded = false;
      try {
        const res = await api.get('/TimeRegistrationGroup', { params: { includeArchived: this.includeArchived } });
        this.groups = res.data;
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      } finally {
        this.loaded = true;
      }
    },
    async fetchUsers() {
      try {
        // /User returns a PaginatedList ({ result, totalItems, ... }) — unwrap .result
        const res = await api.get('/User', { params: { pageSize: 500, includeDisabled: false, excludeImages: true } });
        this.users = res.data?.result || [];
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
      }
    },
    openCreate() {
      this.editMode = false;
      this.form = { id: null, name: '', description: '', isArchived: false };
      this.showForm = true;
    },
    openEdit(row) {
      this.editMode = true;
      this.form = { id: row.id, name: row.name, description: row.description || '', isArchived: row.isArchived };
      this.showForm = true;
    },
    async submitForm() {
      if (!this.form.name || !this.form.name.trim()) {
        this.$toast.danger(this.$t('timeRegistrationGroups.nameRequired'), { timeout: 3000 });
        return;
      }
      this.saving = true;
      try {
        if (this.editMode) {
          await api.put(`/TimeRegistrationGroup/${this.form.id}`, {
            name: this.form.name.trim(),
            description: this.form.description ? this.form.description.trim() : null,
            isArchived: this.form.isArchived,
          });
        } else {
          await api.post('/TimeRegistrationGroup', {
            name: this.form.name.trim(),
            description: this.form.description ? this.form.description.trim() : null,
          });
        }
        this.$toast.success(this.$t('common.saved'), { timeout: 2000 });
        this.showForm = false;
        await this.fetchGroups();
      } catch (err) {
        this.$toast.danger(err?.response?.data || this.$t('common.unableToUpdate'), { timeout: 0 });
      } finally {
        this.saving = false;
      }
    },
    async confirmDelete(row) {
      try {
        await ElMessageBox.confirm(
          this.$t('timeRegistrationGroups.deleteConfirm', { name: row.name }),
          this.$t('common.confirm'),
          { type: 'warning' }
        );
      } catch { return; }
      try {
        await api.delete(`/TimeRegistrationGroup/${row.id}`);
        this.$toast.success(this.$t('common.deleted'), { timeout: 2000 });
        await this.fetchGroups();
      } catch (err) {
        this.$toast.danger(err?.response?.data || this.$t('common.unableToDelete'), { timeout: 0 });
      }
    },
    openMembers(row) {
      this.activeGroupId = row.id;
      this.selectedUserToAdd = null;
      this.showMembers = true;
    },
    async addMember() {
      if (!this.selectedUserToAdd || !this.activeGroup) return;
      this.savingMember = true;
      try {
        await api.post(`/TimeRegistrationGroup/${this.activeGroup.id}/members`, { userId: this.selectedUserToAdd });
        this.$toast.success(this.$t('common.saved'), { timeout: 2000 });
        this.selectedUserToAdd = null;
        await this.fetchGroups();
      } catch (err) {
        this.$toast.danger(err?.response?.data || this.$t('common.unableToUpdate'), { timeout: 0 });
      } finally {
        this.savingMember = false;
      }
    },
    async removeMember(member) {
      if (!this.activeGroup) return;
      try {
        await api.delete(`/TimeRegistrationGroup/${this.activeGroup.id}/members/${member.userId}`);
        this.$toast.success(this.$t('common.deleted'), { timeout: 2000 });
        await this.fetchGroups();
      } catch (err) {
        this.$toast.danger(err?.response?.data || this.$t('common.unableToDelete'), { timeout: 0 });
      }
    },
  },
};
</script>
